import type { AnalyticsMode } from './consent'

/** Initiative observation window; not canonical activation or retention policy. */
export const ACQUISITION_WINDOW_DAYS = 28
export const ACQUISITION_LANDING_EVENT = 'acquisition_landing_engaged'

// Measurement allowlist only; never changes routing or authorization.
const PATHS = new Set([
  '/',
  '/es',
  '/fr',
  '/pricing',
  '/es/pricing',
  '/fr/pricing',
  '/alumni-engagement-software',
  '/alumni-management-software',
  '/k12-alumni-software',
  '/es/plataforma-de-exalumnos',
  '/solutions/universities',
  '/solutions/international-schools',
  '/es/red-de-exalumnos-para-colegios',
  '/es/red-de-exalumnos-para-universidades',
  '/compare/alumnia-vs-hivebrite',
  '/compare/alumnia-vs-graduway',
  '/compare/alumnia-vs-almabase',
  '/compare/alumnia-vs-toucantech',
  '/features/alumni-mentoring',
  '/es/features/mentorias-entre-egresados',
  '/features/alumni-directory',
  '/es/features/directorio-de-exalumnos',
  '/features/alumni-job-board',
  '/es/features/empleos-para-egresados',
  '/blog',
  '/es/blog',
])
export function acquisitionLandingPath(path: string): string | null {
  if (PATHS.has(path)) return path
  // Arbitrary URL text is not sent. Blog articles use a bounded route family.
  if (/^\/(es\/)?blog\/[a-z0-9-]+$/.test(path))
    return path.startsWith('/es/') ? '/es/blog/[post]' : '/blog/[post]'
  return null
}
export const ACQUISITION_SOURCES = [
  'google',
  'bing',
  'chatgpt',
  'perplexity',
  'claude',
  'gemini',
  'linkedin',
  'newsletter',
  'email',
] as const
export const ACQUISITION_MEDIUMS = ['email', 'social', 'cpc', 'organic', 'referral']
export const ACQUISITION_CAMPAIGNS = [
  'launch',
  'mentoring',
  'directory',
  'jobs',
  'pricing',
  'comparison',
]
export type AcquisitionSource = (typeof ACQUISITION_SOURCES)[number] | 'other' | 'unknown'
function knownUtm(value: string | null, allowed: readonly string[]): string | undefined {
  if (!value) return undefined
  const normalized = value.trim().toLowerCase()
  return allowed.includes(normalized) ? normalized : 'other'
}
export function acquisitionAttribution(search: string, referrer: string): Record<string, string> {
  const query = new URLSearchParams(search)
  const source = knownUtm(query.get('utm_source'), ACQUISITION_SOURCES)
  let acquisitionSource: AcquisitionSource = (source as AcquisitionSource | undefined) ?? 'unknown'
  if (!source && referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase()
      const known: Record<string, AcquisitionSource> = {
        'www.google.com': 'google',
        'google.com': 'google',
        'www.bing.com': 'bing',
        'bing.com': 'bing',
        'chatgpt.com': 'chatgpt',
        'chat.openai.com': 'chatgpt',
        'www.perplexity.ai': 'perplexity',
        'perplexity.ai': 'perplexity',
        'claude.ai': 'claude',
        'gemini.google.com': 'gemini',
        'www.linkedin.com': 'linkedin',
        'linkedin.com': 'linkedin',
      }
      acquisitionSource =
        known[host] ??
        (host === 'alumnia.net' || host.endsWith('.alumnia.net') ? 'unknown' : 'other')
    } catch {
      /* missing/invalid source stays unknown, never assumed direct */
    }
  }
  return {
    acquisition_source: acquisitionSource,
    ...(source ? { utm_source: source } : {}),
    ...(query.get('utm_medium')
      ? { utm_medium: knownUtm(query.get('utm_medium'), ACQUISITION_MEDIUMS)! }
      : {}),
    ...(query.get('utm_campaign')
      ? { utm_campaign: knownUtm(query.get('utm_campaign'), ACQUISITION_CAMPAIGNS)! }
      : {}),
  }
}
export interface AcquisitionSdk {
  __loaded?: boolean
  capture: (name: string, props: Record<string, unknown>) => unknown
}
/** Public telemetry; the optional callback stores a bounded consented touch, never an identity. */
export function createAcquisitionTracker(
  sdk: AcquisitionSdk,
  onConsentedEngagement?: (properties: Record<string, string>) => void
) {
  const engaged = new Set<string>()
  return {
    consentChanged() {
      engaged.clear()
    },
    engage(args: {
      pathname: string
      search: string
      referrer: string
      mode: AnalyticsMode
      trusted: boolean
      visible: boolean
      webdriver: boolean
      userAgent: string
      release: string
    }) {
      const landing = acquisitionLandingPath(args.pathname)
      if (
        !sdk.__loaded ||
        !landing ||
        !args.trusted ||
        !args.visible ||
        args.webdriver ||
        /bot|crawler|spider|headless|preview/i.test(args.userAgent)
      )
        return
      const attribution =
        args.mode === 'full'
          ? acquisitionAttribution(args.search, args.referrer)
          : { acquisition_source: 'unknown' }
      if (args.mode === 'full')
        onConsentedEngagement?.({
          landing_path: landing,
          landing_release: args.release,
          ...attribution,
        })
      if (engaged.has(args.pathname)) return
      engaged.add(args.pathname)
      sdk.capture(ACQUISITION_LANDING_EVENT, {
        landing_path: landing,
        release: args.release,
        attribution_mode: args.mode === 'full' ? 'consented' : 'unlinked',
        ...attribution,
        // Override stale institution super-properties and raw SDK URLs on this public-only event.
        org_id: null,
        org_slug: null,
        org_is_demo: null,
        member_role: null,
        $groups: {},
        $current_url: `https://alumnia.net${landing}`,
        $pathname: landing,
        $referrer: '',
        $initial_referrer: '',
        $initial_current_url: '',
      })
    },
  }
}

/** Applied after SDK decoration: raw session URLs/$set_once otherwise bypass capture props. */
export function publicAcquisitionEvent<
  E extends {
    event: string
    properties: Record<string, unknown>
    $set?: unknown
    $set_once?: unknown
  },
>(event: E): E {
  if (event.event !== ACQUISITION_LANDING_EVENT) return event
  const allowed = [
    'distinct_id',
    'token',
    'landing_path',
    'release',
    'attribution_mode',
    'acquisition_source',
    'app_env',
    'app_platform',
    'ui_version',
  ]
  const properties: Record<string, unknown> = { $process_person_profile: false }
  for (const key of allowed) if (key in event.properties) properties[key] = event.properties[key]
  if (properties.attribution_mode === 'consented') {
    for (const [key, values] of [
      ['utm_source', ACQUISITION_SOURCES],
      ['utm_medium', ACQUISITION_MEDIUMS],
      ['utm_campaign', ACQUISITION_CAMPAIGNS],
    ] as const) {
      const raw = event.properties[key]
      if (typeof raw === 'string' && raw) properties[key] = knownUtm(raw, values)
    }
  }
  const clean = { ...event, properties }
  delete clean.$set
  delete clean.$set_once
  return clean
}
